package fr.frux.feature.treeadd.wizard

import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.inSeasonHighlights
import fr.frux.shared.domain.model.offeredForTrees

/** Délai de la recherche d'espèce et longueur minimale de la saisie avant d'interroger le référentiel. */
const val SEARCH_DEBOUNCE_MS = 300L
const val MIN_SEARCH_QUERY_LENGTH = 2

/** Rayon et plafond de l'appel unique qui compte les espèces autour de la position figée. */
const val NEARBY_SPECIES_RADIUS_METERS = 1_000.0
const val NEARBY_SPECIES_LIMIT = 1_000

/** Douze tuiles, avec ou sans recherche : la recherche reste au-dessus, pas de tuile « Autre espèce » (#647). */
const val MAX_AROUND_TILES = 12
const val MAX_SEARCH_TILES = 12

/** Arbres par identifiant d'espèce exact : les variétés ne se replient pas sur leur espèce. */
fun countBySpecies(trees: List<Tree>): Map<Long, Int> = trees.groupingBy { it.species.id }.eachCount()

private fun byCountThenName(counts: Map<Long, Int>?): Comparator<Species> =
    compareByDescending<Species> { counts?.get(it.id) ?: 0 }.thenBy(String.CASE_INSENSITIVE_ORDER) { it.commonNameFr }

/**
 * Grille du coin : les espèces sélectionnables comptées au moins une fois, les plus courantes
 * d'abord, puis les espèces de saison absentes du coin (« aucun ici ») jusqu'à [MAX_AROUND_TILES],
 * pour qu'un coin presque vide propose quand même de quoi choisir (#647). Sans aucune comptée, la
 * grille est de saison seule (compte 0, ou `null` si [counts] est inconnu parce que l'appel a
 * échoué) : elle ne reste jamais vide.
 */
fun aroundGrid(
    referential: List<Species>,
    counts: Map<Long, Int>?,
    month: Int,
): SpeciesGrid {
    val around =
        referential
            .filter { it.offeredForTrees && (counts?.get(it.id) ?: 0) > 0 }
            .sortedWith(byCountThenName(counts))
            .take(MAX_AROUND_TILES)
    val fallbackCount = if (counts == null) null else 0
    val aroundIds = around.map { it.id }.toSet()
    val season =
        referential
            .filter { it.id !in aroundIds }
            .inSeasonHighlights(month, limit = MAX_AROUND_TILES - around.size)
            .map { SpeciesTileItem(it, fallbackCount) }
    if (around.isNotEmpty()) {
        return SpeciesGrid(SpeciesGridKind.AROUND, around.map { SpeciesTileItem(it, counts?.get(it.id)) } + season)
    }
    return SpeciesGrid(SpeciesGridKind.SEASON_FALLBACK, season)
}

/** Résultats de recherche, dans le même ordre que la grille du coin, nom surligné. */
fun searchGrid(
    referential: List<Species>,
    counts: Map<Long, Int>?,
    query: String,
): SpeciesGrid {
    val tiles =
        referential
            .filter { it.offeredForTrees && it.matches(query) }
            .sortedWith(byCountThenName(counts))
            .take(MAX_SEARCH_TILES)
            .map { SpeciesTileItem(it, counts?.let { c -> c[it.id] ?: 0 }, matchRange(it.commonNameFr, query)) }
    return SpeciesGrid(if (tiles.isEmpty()) SpeciesGridKind.NO_RESULT else SpeciesGridKind.SEARCH, tiles)
}

/** Première occurrence de [query] dans [text], sans tenir compte de la casse. */
fun matchRange(
    text: String,
    query: String,
): IntRange? {
    val start = text.indexOf(query, ignoreCase = true)
    return if (start < 0 || query.isEmpty()) null else start until start + query.length
}
